import React, { useEffect, useState, useMemo, useCallback } from 'react';
import styled from 'styled-components'
import { Button, Layout, Grid, Stat, LazyBoi, Tabbed, Panel, GateKeeper, Amount, Modal } from "@components";
import { SwapController } from './_controller'
import { Asset } from '@archetypes'
import { fromWei, formatCommas } from '@util/helpers'
import assetController, { Status } from '@app/App.Controller'
import { ReactComponent as Refresh } from '@assets/Icon_Refresh.svg';
import { ReactComponent as ChevronDown } from '@assets/Icon_Chevron_Down2.svg';
import useModal from "../../hooks/useModal"
 

const PresetsWrapper = styled.div`
    width: 50%; 
    margin-top: 10px;
    margin-bottom: 20px;
    height:  30px;

    button {
        font-size: 12px;
    }

    
`

const BuyPanel = ({ balances, liquidityPools, updateParams, updateLoading }) => {

    const { balancer } = assetController

    const [approvalStatus, setApprovalStatus] = useState()
    const [approvalParams, setApprovalParams] = useState()
    const [controller, setController] = useState()
    const [swapParams, setSwapParams] = useState()
    const { open, openModal, closeModal } = useModal();
    const [baseToken, setBaseToken] = useState()
    const [selectedLiquidityPoolAddress, setSelectedLiquidityPoolAddress] = useState()

    useEffect(() => {

        if (selectedLiquidityPoolAddress) {
            const swapController = new SwapController(selectedLiquidityPoolAddress, "sell", assetController)
            swapController.on('approvals', setApprovalParams)
            swapController.on('update', setSwapParams)
            setController(swapController)
            swapController.init()
            return () => swapController.cleanup()
        }

    }, [selectedLiquidityPoolAddress]) // eslint-disable-line

    useEffect(() => {
        if (swapParams) {
            updateParams(swapParams)
        }
    }, [swapParams])

    const tokens = useMemo(() => {
        let tokens = []
        if (liquidityPools.length > 0) {
            for (let pool of liquidityPools) {
                if (!tokens.find(item => item.symbol === pool?.token1?.symbol)) {
                    tokens.push({
                        symbol: pool?.token1?.symbol,
                        balance: balances ? balances[pool?.token1?.symbol]?.balance : null
                    })
                }
            }
        }

        if (balances) {
            closeModal()
        }
        return tokens
    }, [liquidityPools, balances])

    useEffect(() => {
        if (tokens.length === 0) {
            updateLoading(true)
        } else {
            updateLoading(false)
        }
    }, [tokens])

    useEffect(() => {
        if (baseToken && liquidityPools.length > 0) {
            const { address } = liquidityPools.find(pool => pool?.token1?.symbol === baseToken)
            if (address) {
                setSelectedLiquidityPoolAddress(address)
            }
        }
    }, [baseToken, liquidityPools])

    const onSelect = useCallback((selected) => {
        if (selected !== baseToken) {
            setBaseToken(selected)
            updateParams()
        }
        closeModal()

    }, [baseToken])

    const perlBalanceInfo = balances && balances["PERL"] ? `Balance : ${Number(fromWei(balances["PERL"].balance)).toLocaleString()} PERL` : ""
    const baseTokenBalance = (balances && balances[baseToken]) ? `Max : ${Number(fromWei(balances[baseToken].balance)).toLocaleString()} ${baseToken}` : ""

    return (
        <>
            <Amount.GroupSelect
                className="group-input"
                info="SWAP"
                infoEnd={baseTokenBalance}
                min={0}
                disabled={!controller}
                value={swapParams?.inputAmount}
                onChange={amount => controller?.updateFromAmount(amount)}
                max={fromWei(swapParams?.balance)}
                selection={
                    !baseToken
                        ?
                        <Button.Primary
                            onClick={openModal}
                            disabled={tokens && tokens.length === 0}
                        >
                            Select Token&nbsp;&nbsp;<ChevronDown />
                        </Button.Primary>
                        :
                        <Asset.Icon
                            onClick={openModal}
                            style={{ cursor: "pointer" }}
                            token={baseToken}
                        />
                }
            />
            <PresetsWrapper>
                <Amount.Presets
                    onChange={pc => controller?.updateInputPercent(pc)}
                    value={swapParams?.inputPercent}
                    small={true}
                    disabled={!swapParams?.balance}
                />
            </PresetsWrapper>

            <Amount.GroupSelect
                className="group-input"
                info="FOR"
                value={fromWei(swapParams?.toAmount)}
                max={swapParams?.maxToAmount}
                disabled={!swapParams?.balance}
                onChange={amount => controller?.updateToAmount(amount)} 
                infoEnd={perlBalanceInfo}
                selection={
                    <Asset.Icon
                        token={"PERL"}
                    />
                }
            />
            <GateKeeper
                assertions={[
                    {
                        assert: () => tokens && tokens.length > 0,
                        // onFail: () => <><Refresh className='rotate' /> Fetching available token pairs...</>,
                    },
                ]}
                dependencies={[tokens]}
            >
                <PanelFooter
                    action="sell"
                    approvalParams={approvalParams}
                    setApprovalStatus={setApprovalStatus}
                    swapParams={swapParams}
                    approvalStatus={approvalStatus}
                    balancer={balancer}
                />
            </GateKeeper>
            {open ? (
                <Modal.TokenSelection
                    close={closeModal}
                    onSelect={onSelect}
                    tokens={tokens}
                />
            ) : null}
        </>
    )
}

const SellPanel = ({ balances, liquidityPools, updateParams, updateLoading }) => {

    const { balancer } = assetController

    const [approvalStatus, setApprovalStatus] = useState()
    const [approvalParams, setApprovalParams] = useState()
    const [controller, setController] = useState()
    const [swapParams, setSwapParams] = useState()
    const { open, openModal, closeModal } = useModal();
    const [pairToken, setPairToken] = useState()
    const [selectedLiquidityPoolAddress, setSelectedLiquidityPoolAddress] = useState()

    useEffect(() => {

        if (selectedLiquidityPoolAddress) {
            const swapController = new SwapController(selectedLiquidityPoolAddress, "buy", assetController)
            swapController.on('approvals', setApprovalParams)
            swapController.on('update', setSwapParams)
            setController(swapController)
            swapController.init()
            return () => swapController.cleanup()
        }

    }, [selectedLiquidityPoolAddress]) // eslint-disable-line

    useEffect(() => {
        if (swapParams) {
            updateParams(swapParams)
        }
    }, [swapParams])

    const tokens = useMemo(() => {
        let tokens = []
        if (liquidityPools.length > 0) {
            for (let pool of liquidityPools) {
                if (!tokens.find(item => item.symbol === pool?.token1?.symbol)) {
                    tokens.push({
                        symbol: pool?.token1?.symbol,
                        balance: balances ? balances[pool?.token1?.symbol]?.balance : null
                    })
                }
            }
        }
        if (balances) {
            closeModal()
        }
        return tokens
    }, [liquidityPools, balances])

    useEffect(() => {
        if (tokens.length === 0) {
            updateLoading(true)
        } else {
            updateLoading(false)
        }
    }, [tokens])

    useEffect(() => {
        if (pairToken && liquidityPools.length > 0) {
            const { address } = liquidityPools.find(pool => pool?.token1?.symbol === pairToken)
            if (address) {
                setSelectedLiquidityPoolAddress(address)
            }
        }
    }, [pairToken, liquidityPools])

    const onSelect = useCallback((selected) => {
        if (selected !== pairToken) {
            setPairToken(selected)
            updateParams()
        }
        closeModal()
    }, [pairToken])


    const perlBalanceInfo = balances && balances["PERL"] ? `Max : ${Number(fromWei(balances["PERL"].balance)).toLocaleString()} PERL` : ""

    return (
        <>
            <Amount.GroupSelect
                className="group-input"
                info="SWAP"
                min={0}
                disabled={!controller}
                onChange={amount => controller?.updateFromAmount(amount)}
                infoEnd={perlBalanceInfo}
                value={swapParams?.inputAmount}
                max={balances ? fromWei(balances["PERL"].balance) : 0}
                selection={
                    <Asset.Icon
                        token={"PERL"}
                    />
                }
            />
            <PresetsWrapper>
                <Amount.Presets
                    onChange={pc => controller?.updateInputPercent(pc)}
                    small={true}
                    value={swapParams?.inputPercent}
                    disabled={!swapParams?.balance}
                />
            </PresetsWrapper>
            <Amount.GroupSelect
                className="group-input"
                info="FOR"
                disabled={!swapParams?.balance}
                max={swapParams?.maxToAmount}
                value={fromWei(swapParams?.toAmount)}
                onChange={amount => controller?.updateToAmount(amount)} 
                selection={
                    !pairToken
                        ?
                        <Button.Primary
                            onClick={openModal}
                        >
                            Select Token&nbsp;&nbsp;<ChevronDown />
                        </Button.Primary>
                        :
                        <Asset.Icon
                            onClick={openModal}
                            style={{ cursor: "pointer" }}
                            token={pairToken}
                        />
                }
            />

            <GateKeeper
                assertions={[
                    {
                        assert: () => tokens && tokens.length > 0,
                        // onFail: () => <><Refresh className='rotate' /> Fetching available token pairs...</>,
                    },
                ]}
                dependencies={[tokens]}
            >
                <PanelFooter
                    action="buy"
                    approvalParams={approvalParams}
                    setApprovalStatus={setApprovalStatus}
                    swapParams={swapParams}
                    approvalStatus={approvalStatus}
                    balancer={balancer}
                />
            </GateKeeper>

            {open ? (
                <Modal.TokenSelection
                    close={closeModal}
                    onSelect={onSelect}
                    tokens={tokens}
                />
            ) : null}
        </>
    )
}


const Params = styled.div`
    background-color: #132A3E;
    display: flex;
    padding: 0;
    max-height: 0;
    overflow: hidden;
    transition: all 0.5s;
    flex-direction: column;
    margin-top: -5px;
    border-bottom-left-radius: 8px;
    border-bottom-right-radius: 8px;

    font-size: 14px;
    font-weight: 400;
    
    .-loading {
        padding: 5px;
        text-align: center;
        color: var(--color-label);

    }

    .-row {
        display: flex;
        flex-direction: row;
        padding: 20px;
        padding-top: 5px;
        padding-bottom: 5px;
        >div { 
            flex: 30%;
            color: var(--color-label);
            :last-child {
                flex: 70%;
                color: var(--color-white);
                text-align: right;
            }
        }
    }

    ${props => props.visible && `
        max-height: 200px;
        padding: 20px;
    `}

`

const PanelFooter = ({
    action,
    approvalParams,
    setApprovalStatus,
    swapParams,
    approvalStatus,
    balancer
}) => {

    const isActive = +swapParams?.balance > 0

    const onSubmit = useCallback(() => {
        if (swapParams && swapParams.fromAmount !== "0") {
            balancer.swap(swapParams.poolAddress, swapParams.tx)
        }
    }, [swapParams])

    return (
        <>
            <Asset.ContractApproval.Wizard
                disabled={!isActive}
                approvals={approvalParams}
                onUpdate={setApprovalStatus}
                actionText={"Swap Tokens"}
                onAction={() => onSubmit()}
            />
        </>
    )
}

const ActionPanel = styled(
    ({ className, action }) => {

        const { wallet, account, perlinx } = assetController
        const [balances, setBalances] = useState()
        const [address, setAddress] = useState()
        const [liquidityPools, setLiquidityPools] = useState([])
        const [params, setParams] = useState()
        const [loading, setLoading] = useState(false)

        useEffect(() => {
            const balanceSub = wallet.state.subscribe('balances', balances => setBalances({ ...balances }), true)
            const addressSub = account.state.subscribe('address', address => setAddress(address), true)
            const LPsub = perlinx.state.subscribe(`pools`, pools => { 
                const filtered = Object.values(pools).filter(item => item.name !== "PX-PERL-pxGOLD_MAR2022" && item.name !== "PX-PERL-pxUSD_MAR2022")
                setLiquidityPools(filtered)
            }, true)
            return () => {
                balanceSub.unsubscribe()
                addressSub.unsubscribe()
                LPsub.unsubscribe()
            }
        }, []) // eslint-disable-line

        return (
            <>
                <Panel className={className}>
                    <div style={{ padding: 20, paddingTop: 10 }}>
                        {action === "buy" && <BuyPanel balances={balances} address={address} liquidityPools={liquidityPools} updateParams={setParams} updateLoading={setLoading} />}
                        {action === "sell" && <SellPanel balances={balances} address={address} liquidityPools={liquidityPools} updateParams={setParams} updateLoading={setLoading} />}
                    </div>
                </Panel>
                <Params
                    visible={loading}
                >
                    {loading && (<div className="-loading"><Refresh className='rotate' style={{ marginRight: 5 }} /> Fetching available token pairs...</div>)}
                </Params>
                <Params
                    visible={params !== undefined}
                >
                    <div className="-row">
                        <div>Price</div>
                        <div>
                            {`${params?.tokenSell?.price.toLocaleString()} ${params?.tokenBuy?.symbol} per ${params?.tokenSell?.symbol}`}
                            <br />
                            {`${params?.tokenBuy?.price.toLocaleString()} ${params?.tokenSell?.symbol} per ${params?.tokenBuy?.symbol}`}
                        </div>
                    </div>
                    <div className="-row">
                        <div>Slip</div>
                        <div>
                            <LazyBoi value={params?.slippage} suffix={'%'} fromWei />
                        </div>
                    </div>
                    <div className="-row">
                        <div>Liquidity Provider Fee</div>
                        <div>
                            <LazyBoi.Wei value={params?.providerFee} suffix={params?.tokenSell?.symbol} dp={2} />
                        </div>
                    </div>
                </Params>
            </>
        )
    })`
        margin-top: 2rem;
    `

const Exchange = styled(
    ({ className }) => {
        return (
            <Tabbed className={`${className}`} center={true}>
                <Tabbed.Child title={`Buy PERL`}>
                    <ActionPanel action={'buy'} />
                </Tabbed.Child>
                <Tabbed.Child title={`Sell PERL`}>
                    <ActionPanel action={'sell'} />
                </Tabbed.Child>
            </Tabbed>
        )
    })`

    max-width: 600px;
	margin-left: auto;
	margin-right: auto;
    
    `



export default Exchange